import React, { useState } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    CircularProgress,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc } from 'firebase/firestore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { storage, db } from '../initialFirebase';
import TitleAndContent from './TitleAndContent';

export default function FeedbackSection({ feedBackSectionRef }) {

    const [feedback, setFeedback] = useState('');
    const [screenshots, setScreenshots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png']
        },
        onDrop: (acceptedFiles) => {
            setScreenshots(acceptedFiles);
        }
    });

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const screenshotUrls = [];

            // Upload screenshots
            for (const screenshot of screenshots) {
                const storageRef = ref(storage, `screenshots/${Date.now()}-${screenshot.name}`);
                await uploadBytes(storageRef, screenshot);
                const url = await getDownloadURL(storageRef);
                screenshotUrls.push(url);
            }

            // Save feedback to Firestore
            await addDoc(collection(db, 'ezclean_feedback'), {
                feedback,
                screenshots: screenshotUrls,
                createdAt: new Date().toISOString(),
            });
            setFeedback('')
            setScreenshots([])
            setOpenSnackbar(true)
        } catch (err) {
            setError('Failed to submit feedback. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack
            ref={feedBackSectionRef}
            sx={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                height: '100vh'
            }}>
            <Snackbar open={openSnackbar} autoHideDuration={5000} onClose={handleClose}>
                <Alert
                    onClose={handleClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    <Typography variant='h5'>
                        Submit successfully!
                    </Typography>
                </Alert>
            </Snackbar>
            <TitleAndContent title={"Your insight, our growth"} content={"Be the first to share your experience with our app!\nYour feedback helps us improve and provide better service to all users."} />
            <Paper
                elevation={0}
                sx={{
                    width: '50%',
                    height: '50%',
                    p: 4,
                    borderRadius: 2,
                    background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
                }}
            >
                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            fullWidth
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            multiline
                            rows={6}
                            required
                            focused
                            placeholder='Input your feedback...'
                            variant="outlined"
                            inputProps={{ style: { fontSize: "1.8rem" } }}
                            InputLabelProps={{
                                style: { fontSize: "2rem" },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'linear-gradient(to right bottom, #57F1B3, #00D4F6)',
                                    },
                                },
                            }}
                        />
                        <Box
                            {...getRootProps()}
                            sx={{
                                border: '2px dashed',
                                borderColor: isDragActive ? 'linear-gradient(to right bottom, #57F1B3, #00D4F6)' : 'rgba(0, 0, 0, 0.12)',
                                borderRadius: 2,
                                p: 4,
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                background: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                '&:hover': {
                                    borderColor: 'linear-gradient(to right bottom, #57F1B3, #00D4F6)',
                                    background: 'rgba(25, 118, 210, 0.04)',
                                }
                            }}
                        >
                            <input {...getInputProps()} />
                            <CloudUploadIcon
                                sx={{
                                    fontSize: 48,
                                    color: 'linear-gradient(to right bottom, #57F1B3, #00D4F6)',
                                    mb: 2
                                }}
                            />
                            <Typography variant="h4" gutterBottom>
                                {isDragActive
                                    ? 'Drop the screenshots here'
                                    : 'Drag and drop screenshots here'}
                            </Typography>
                            <Typography variant="h5" color="text.secondary">
                                or click to select files
                            </Typography>
                            {screenshots.length > 0 && (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mt: 2,
                                        color: 'linear-gradient(to right bottom, #57F1B3, #00D4F6)',
                                        fontWeight: 700
                                    }}

                                >
                                    {screenshots.length} file(s) selected
                                </Typography>
                            )}
                        </Box>
                        {error && (
                            <Typography
                                color="error"
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    background: 'rgba(211, 47, 47, 0.1)'
                                }}
                            >
                                {error}
                            </Typography>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            fullWidth
                            sx={{
                                mt: 2,
                                py: 1.5,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1.1rem',
                                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.2)',
                                '&:hover': {
                                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.3)',
                                },
                                background: 'linear-gradient(to right bottom, #57F1B3, #00D4F6)',
                            }}
                        >
                            {loading ? <CircularProgress size={24} /> : <Typography
                                variant="h5"
                                sx={{ fontWeight: 700 }}
                            >
                                Submit feedback
                            </Typography>}
                        </Button>
                    </Box>
                </form>
            </Paper>
        </Stack>
    )
}